/**
 * Orders List component.
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Location } from '@angular/common';
import { NgForm } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { WdLocalEnvironmentService } from '../../../../services/environment/wd-local-environment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { WdLocalSharedFunctionsService } from '../../../../services/shared/wd-local-shared-functions.service';
import { WdOrdersV2Service } from '../../../../services/orders/wd-orders-v2.service';
import {
  IListOrdersViewTranslations,
  IOrderModel,
} from '../../../../interfaces/orders/order-interfaces';
import {
  IBcxErrorService,
  IBcxSharedFunctionsService,
} from '../../../../interfaces/generic/generic-interfaces';
import { WdVehicleAssignmentService } from '../../../../services/vehicles/assignments/wd-vehicle-assignment.service';
import { MatDialog } from '@angular/material/dialog';
import { OrderAssignmentsComponent } from '../../../../components/modals/order-assignments/order-assignments.component';
import { OrderAdjustmentsComponent } from '../../../../components/modals/order-adjustments/order-adjustments.component';

@Component({
  selector: 'app-orders-list-v2-component',
  templateUrl: './list-v2.component.html',
  styleUrls: ['../../orders.component.scss', './list-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListV2Component implements OnDestroy, OnInit, AfterViewInit {
  // Basic Variables
  pageTitle: string;
  pageSubtitle: string;

  // Booleans
  sortInitialized = false;

  // Common Translations
  commonTranslations: IListOrdersViewTranslations = {};

  // Order Items
  orderListDataSource: MatTableDataSource<any>;

  // Order Types
  orderType: string = this.wdLocalSharedFunctionsService.orderTypeKey.WORK;

  // Subscriptions
  subscriptions = new Subscription();

  // View Children
  @ViewChild(MatSort) sort: MatSort; // Table Sorting
  @ViewChild('ordersListForm', { static: true }) public ordersListForm!: NgForm; // Form

  // Column Toggle Options
  columnHeaders: Array<string> = [];

  /**
   * Constructor Initialization
   */
  constructor(
    private matDialog: MatDialog,
    public changeDetector: ChangeDetectorRef,
    private location: Location,
    private translate: TranslateService,
    public activatedRoute: ActivatedRoute,
    public wdLocalSharedFunctionsService: WdLocalSharedFunctionsService,
    public wdEnvironmentService: WdLocalEnvironmentService,
    public wdOrdersService: WdOrdersV2Service,
    public wdVehicleAssignmentService: WdVehicleAssignmentService,
    public router: Router,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdSharedFunctionsService')
    public wdSharedFunctionsService: IBcxSharedFunctionsService,
  ) {}

  /*********************************************** Async Functions ****************************************************/

  /**
   * Update fulfillment group status
   * @since 4.71.0
   */
  async updateFulfillmentGroupStatus(
    element: any,
    orderGroup: any,
  ): Promise<void> {
    orderGroup.isUpdating = true;

    try {
      // Update Status
      await this.wdOrdersService.updateFulfillmentGroupStatus(
        element.index,
        orderGroup?.index,
        orderGroup?.status?.index,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersService.fetchOrders(false);
      orderGroup.isUpdating = false;
    }
  }

  /**
   * Update order date
   * @since 4.71.0
   * @memberof ListV2Component
   */
  async updateOrderDate(
    element: IOrderModel,
    type: string,
    target?: string,
  ): Promise<void> {
    element[type].isUpdatingOrder = true;
    try {
      // Update Date
      await this.wdOrdersService.updateOrderDate(
        element.index,
        type,
        target,
        target ? element[type][target].date : element[type].date,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersService.fetchOrders(false);
      element[type].isUpdatingOrder = false;
    }
  }

  /**
   * Update order shipping loading zone
   * @since 4.71.0
   */
  async updateShippingLoadingZone(element: any): Promise<void> {
    element.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersService.updateShippingLoadingZone(
        element.index,
        element.delivery?.loadingZone?.id,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersService.fetchOrders(false);
      element.isUpdating = false;
    }
  }

  /**
   * Update order shipping option
   * @since 4.71.0
   * @memberof ListV2Component
   */
  async updateShippingOption(element: any): Promise<void> {
    element.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersService.updateShippingOption(
        element.index,
        element.delivery?.option?.value,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersService.fetchOrders(false);
      element.isUpdating = false;
    }
  }

  /**
   * Update order shipping status
   * @since 4.71.0
   * @memberof ListV2Component
   */
  async updateShippingStatus(element: any): Promise<void> {
    element.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersService.updateShippingStatus(
        element.index,
        element.delivery?.status?.index,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersService.fetchOrders(false);
      element.isUpdating = false;
    }
  }

  /**
   * Update order status
   * @since 4.71.0
   * @memberof ListV2Component
   */
  async updateOrderStatus(element: any): Promise<void> {
    element.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersService.updateOrderStatus(
        element.index,
        element.status?.type,
      );
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersService.fetchOrders(false);
      element.isUpdating = false;
    }
  }

  /********************************************************************************************************************/

  /**
   * Get assignments column title.
   */
  getAssignmentsColumnTitle(element: any): string {
    const options = element.delivery.vehicle.assignmentOptions ?? [];
    const assignments = element.delivery.vehicle.assignments ?? [];
    // No options available
    if (options.length === 0) {
      return this.translate.instant(
        'order.columns.shipping.assignments.add.description',
      );
    }
    // No options selected
    if (assignments.length === 0) {
      return this.translate.instant(
        'order.columns.shipping.assignments.none.description',
      );
    }
    // One option selected
    if (assignments.length === 1) {
      return assignments[0].labelShort;
    }
    // Multiple options selected
    return `${assignments.length} ${this.translate.instant(
      'order.columns.shipping.assignments.title',
    )}`;
  }

  /**
   * Check if the order groups have matching fulfillment group
   * @since 4.71.0
   * @memberof ListV2Component
   */
  hasMatchingFulfillmentGroup(group: any, orderGroups: any[]): boolean {
    // Check if any order group matches the given group
    return (
      orderGroups &&
      orderGroups.some((orderGroup) => {
        return this.isMatchingFulfillmentGroup(group, orderGroup);
      })
    );
  }

  /**
   * Initialize common view translations
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  initializeCommonViewTranslations(orderType: string): void {
    this.commonTranslations.orders = this.translate.instant(
      'order.title.plural',
    );
    this.commonTranslations.orderType = this.translate.instant(
      'order.types.' + orderType + '.title',
    );
  }

  /**
   * Initialize order type.
   */
  initializeOrderType(orderType: string) {
    // Check for valid order type
    const lookaheadOrderType = this.wdLocalSharedFunctionsService.orderTypeKey.LOOKAHEAD.toString();
    const workOrderType = this.wdLocalSharedFunctionsService.orderTypeKey.WORK.toString();
    const validOrderType = [lookaheadOrderType, workOrderType].includes(
      orderType,
    );
    // Set the order type parameter and redirect if not valid
    if (validOrderType) {
      this.orderType = orderType;
      // Set Order Type Filter
      const allOrderTypes =
        this.wdLocalSharedFunctionsService.orderTypeKey.WORK +
        ',' +
        this.wdLocalSharedFunctionsService.orderTypeKey.LOOKAHEAD;
      // If Work Orders list view, then filter by 'work' and 'spooling' orders.
      // If Spooling Orders list view, then only filter by spooling orders.
      this.wdOrdersService.orderFilters.value.orderType =
        this.orderType === this.wdLocalSharedFunctionsService.orderTypeKey.WORK
          ? allOrderTypes
          : this.orderType;
    } else {
      this.location.replaceState(`/orders/list/${workOrderType}`);
    }
  }

  /**
   * Initialize View Settings
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  initializeSettings() {
    // Initialize route parameters
    this.wdOrdersService.initializeRouteParams();
    // Set visible order columns
    this.wdOrdersService.initColumnToggleOptions(this.orderType);
    // Reset back to page one
    this.wdOrdersService.orderViewSettings.page = 1;
  }

  /**
   * Is called by the view and returns whether the number of selected rows matches the total number of rows.
   * @since 4.71.0
   * @memberof ListV2Component
   */
  isAllSelected(): boolean {
    const numSelected = this.wdOrdersService.allOrdersSelected.selected.length;
    const numRows = this.orderListDataSource.data.length;
    return numSelected === numRows;
  }

  /**
   * Check order fulfillment group matches current group
   * @since 4.71.0
   * @memberof ListV2Component
   */
  isMatchingFulfillmentGroup(group: any, orderGroup: any): boolean {
    return group?.index === orderGroup?.index;
  }

  /**
   * Add new vehicle assignment for order id and date.
   */
  addVehicleAssignmentForOrder(order: IOrderModel) {
    this.wdVehicleAssignmentService
      .addOrderVehicleAssignment(order.id, order.delivery.start.date)
      .then((wasSuccessful) => {
        if (wasSuccessful) {
          // Reload the orders
          void this.wdOrdersService.fetchOrders(false, true);
        }
      });
  }

  /**
   * On Destroy
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  ngOnDestroy() {
    // Unsubscribe from Changes
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
   * After View Initializes
   * @sinic 4.71.0
   */
  ngAfterViewInit(): void {
    // Subscribe to Changes
    this.subscriptions.add(
      this.wdOrdersService.allOrders.subscribe((orders: Array<IOrderModel>) => {
        // Set Order Events/Orders
        this.orderListDataSource = new MatTableDataSource(orders);
        // Developer note: Timeout is required here to fix a known issue where table
        // sorting doesn't work if the table is wrapped in an *ngIf
        setTimeout(() => {
          this.setColumnSortValues(this.sort);
        });
      }),
    );
  }

  /**
   * On Init
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  ngOnInit() {
    // Initialize local storage defaults
    this.wdOrdersService.initializeLocalStorage();
    // Subscribe to route changes and initialize accordingly
    this.subscriptions.add(
      this.activatedRoute.params.subscribe(async (params) => {
        // Initialize order type based on params
        this.initializeOrderType(params.orderType);
        // Initialize common view translations
        this.initializeCommonViewTranslations(params.orderType);
        // Set page titles
        this.setPageTitles();
        // Watch Filters
        await this.wdOrdersService.fetchOrders(true, true);
        // Initialize Order Settings
        this.initializeSettings();
      }),
    );
  }

  /**
   * The label for the checkbox on the input row
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  setCheckboxLabel(row?: any, label?: string): string {
    const deselect = this.translate.instant('actions.deselect');
    const select = this.translate.instant('actions.select');
    const allRows = this.translate.instant('tables.rows.all');
    if (!row) {
      return `${this.isAllSelected() ? deselect : select} ${allRows}`;
    }
    return `${
      this.wdOrdersService.allOrdersSelected.isSelected(row) ? deselect : select
    } ${label}`;
  }

  /**
   * Set Table Sort Values
   * Manually set the sort value for each column (Required on columns where the source is an object)
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  setColumnSortValues(sort: MatSort): void {
    if (sort) {
      // Don't initialize the sort if it's already been changed
      if (!this.sortInitialized) {
        // Set initial column sort value
        this.sort.active = 'number';
        this.sort.direction = 'desc';
        this.sort.sortChange.emit({
          active: this.sort.active,
          direction: this.sort.direction,
        });
        this.sortInitialized = true;
      }
      // Set column sorting
      this.orderListDataSource.sortingDataAccessor = (
        item: any,
        property: string,
      ) => {
        switch (property) {
          case 'assignments':
            if (!item.delivery?.vehicle?.id) {
              return '0-none'; // Sort first by orders with no vehicle set
            } else if (
              !item.delivery?.assignments?.length &&
              !!item.delivery?.vehicle?.id
            ) {
              return '1-add'; // Sort second by vehicle set but no driver set
            } else if (item.delivery?.assignments?.length > 1) {
              return '3-multiple'; // Sort fourth by multiple drivers set
            } else {
              // Sort third by single driver name
              const assignment = item?.delivery?.assignments
                ? item?.delivery?.assignments[0]
                : item.delivery?.vehicle?.id;
              return (
                '2-' +
                (assignment?.driver?.firstName +
                  ' ' +
                  assignment?.driver?.lastName)
              );
            }
          case 'dateOrdered':
            // Sort "Ordered" Column by ordered date
            return Date.parse(item?.ordered?.date);
          case 'dateProductionStart':
            // Sort "Production Date Start" Column by production date start
            return Date.parse(item?.production?.start?.date);
          case 'dateProductionEnd':
            // Sort "Production Date End" Column by production date end
            return Date.parse(item?.production?.end?.date);
          case 'dateRequested':
            // Sort "Requested" Column by requested date
            return Date.parse(item?.requested?.date);
          case 'dateShipping':
            // Sort "Production Date" Column by production date
            return Date.parse(item?.delivery?.date);
          case 'items':
            // Sort "Items" Column by item count
            return item?.meta?.items?.count;
          case 'itemsProgress':
            // Sort "Progress" Column by item count
            return item?.meta?.items?.count;
          case 'jobNumber':
            // Sort "Job Number" Column by job number
            return item?.job?.number?.toLowerCase();
          case 'jobName':
            // Sort "Job Name" Column by job name
            return item?.job?.name?.toLowerCase();
          case 'jobPhase':
            // Sort "Job Number" Column by job number
            return item?.job?.phase?.label?.toLowerCase();
          case 'labour':
            // Sort "Labour" Column by shop labour value
            return item?.totals?.shopLabour?.value;
          case 'labourProgress':
            // Sort "Labour Progress" Column by shop labour value
            return item?.totals?.shopLabourComplete?.value;
          case 'loadingZoneOption':
            // Sort "Loading Zone Option" Column by loading zone option
            return item?.delivery?.loadingZone?.label?.toLowerCase();
          case 'lookahead':
            // Sort "Lookahead" column by lookahead label (a.k.a tag) (ignore case)
            return item?.tag?.toLowerCase();
          case 'orderedBy':
            // Sort "Ordered By" column by user last name (ignore case)
            return item?.orderedBy?.name?.last?.toLowerCase();
          case 'orderedFor':
            // Sort "Ordered For" column by user last name (ignore case)
            return item?.orderedFor?.name?.last?.toLowerCase();
          case 'orderStatus':
            // Sort "Status" Column by status type
            return item?.status?.type;
          case 'productionStatus':
            // Sort "Production Status" Column by production status type
            return item?.production?.status?.index;
          case 'poNumber':
            // Sort "PO number" column by PO number (ignore case)
            return item?.poNumber?.toLowerCase();
          case 'price':
            // Sort "Price" Column by price total
            return item?.totals?.price?.value;
          case 'shippingAddress':
            // Sort "Shipping Address" Column by shipping address
            return item?.delivery?.address?.label?.toLowerCase();
          case 'shippingOption':
            // Sort "Shipping Option" Column by shipping option
            return item?.delivery?.option?.label?.toLowerCase();
          case 'shippingStatus':
            // Sort "Shipping Status" Column by production status type
            return item?.delivery?.status?.index;
          case 'shippingWindowStart':
            // Sort "Shipping Window Start" Column by shipping window start time
            return item?.delivery?.time?.start;
          case 'shippingWindowEnd':
            // Sort "Shipping Window End" Column by shipping window end time
            return item?.delivery?.time?.end;
          case 'tag':
            // Sort "Tag" column by tag (ignore case)
            return item?.tag?.toLowerCase();
          case 'specialInstructions':
            // Sort "Special Instructions" column by special instructions (ignore case)
            return item?.specialInstructions?.toLowerCase();
          case 'userGroup':
            // Sort "User group" column by user group label
            return item?.userGroup?.label?.toLowerCase();
          case 'vehicleOption':
            // Sort "Vehicle Option" Column by vehicle option
            return item?.delivery?.vehicle?.label?.toLowerCase();
          case 'weight':
            // Sort "Weight" Column by weight total
            return item?.totals?.weight?.value;
          case 'shippingAssignments':
            // Sort "Vehicle Assignments" Column by size
            return item?.delivery?.vehicle?.assignments?.length;
          default:
            if (
              property.includes(this.wdOrdersService.fulfillmentColumnPrefix)
            ) {
              // Sort by fulfillment group status
              const fulfillmentGroup = item.fulfillment?.groups.find(
                (group: any) => {
                  return (
                    this.wdOrdersService.fulfillmentColumnPrefix +
                      group.index +
                      '-' +
                      group.code ===
                    property
                  );
                },
              );
              return fulfillmentGroup?.status?.index;
            } else {
              // Sort column by column key
              return item[property];
            }
        }
      };
      this.orderListDataSource.sort = sort;
    }
  }

  /**
   * Set Page Title and Subtitle
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  setPageTitles(): void {
    // Get translations
    const pageSubtitle = this.commonTranslations.orders;
    const pageTitle = this.commonTranslations.orderType;

    // Set page title and subtitle
    this.pageSubtitle = pageSubtitle;
    this.pageTitle = [pageTitle, pageSubtitle].join(' ');

    // Set browser window title
    this.wdSharedFunctionsService.setWindowTitle(
      pageSubtitle + ' - ' + this.wdEnvironmentService.constants.getCompanyName,
    );
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection.
   *
   * @since 4.71.0
   * @memberof ListV2Component
   */
  toggleAllCheckboxes(): void {
    this.isAllSelected()
      ? this.wdOrdersService.allOrdersSelected.clear()
      : this.orderListDataSource.data.forEach((row) => {
          this.wdOrdersService.allOrdersSelected.select(row);
        });
  }

  /**
   * Toggle address popover
   * @since 4.71.0
   * @memberof ListV2Component
   */
  toggleAddressPopover(popover: any, address: any) {
    popover.open({ popover, address });
  }

  /**
   * Toggle adjustments popover
   * @since 4.71.0
   * @memberof ListV2Component
   */
  toggleAdjustmentsPopover(popover: any, count: number) {
    if (popover.isOpen()) {
      popover.close();
    } else {
      if (count) {
        popover.open({ popover, count });
      }
    }
  }

  /**
   * Toggle user details popover
   * @since 4.71.0
   * @memberof ListV2Component
   */
  toggleUserDetailsPopover(popover: any, user: any) {
    popover.open({ popover, user });
  }

  /**
   * Open order adjustments modal.
   */
  openOrderAdjustmentsModal(order: IOrderModel, target: string) {
    // Open dialog
    this.matDialog.open(OrderAdjustmentsComponent, {
      disableClose: true,
      width: '475px',
      data: {
        target: target,
        order: { index: order.index, number: order.number },
        onSuccessCallback: () => {
          // Reload orders to refresh the data
          void this.wdOrdersService.fetchOrders(false);
        },
      },
    });
  }

  /**
   * Open vehicle assignments modal.
   */
  openVehicleAssignmentsModal(order: IOrderModel) {
    // Open dialog
    const dialogRef = this.matDialog.open(OrderAssignmentsComponent, {
      width: '400px',
      maxWidth: '90vw',
      data: {
        orderId: order.id,
        orderNumber: order.number,
        assignments: order.delivery.vehicle.assignments,
        assignmentOptions: order.delivery.vehicle.assignmentOptions,
      },
    });
    // Capture the result
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        // Reload orders to refresh the data
        void this.wdOrdersService.fetchOrders(false);
      }
    });
  }

  /**
   * Toggle Order Override Popover
   * Includes Job Name or Job Number Override
   * @since 4.71.0
   * @memberof ListV2Component
   */
  toggleOrderOverridePopover(
    popover: any,
    order: any,
    type: string,
    label: string,
  ) {
    if (order.type === 'ecommerce') {
      if (!popover.isOpen()) {
        // Set Editable Value and Open Popover
        order.job.override[type + 'Edit'] = order.job.override[type];
        popover.open({ order, popover, type, label });
      }
    }
  }
}
